/**
 * The avatar menu (spec §5): who is signed in, the Ishda / Tanaffus / Ishda emas switch, the
 * person's own sales this week, the profile's sections, and a red Chiqish.
 *
 * The week line is the caller's own number and nothing else: no ranking, no team total, the
 * same basis `Hisobotim` counts on (D9.109). "Agentlik sozlamalari" is the owner's alone.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  Bell,
  Building2,
  Check,
  CircleHelp,
  Keyboard,
  Languages,
  LogOut,
  Monitor,
  Palette,
  TrendingUp,
  UserRound,
} from "lucide-react";
import { useEffect, useState, type JSX } from "react";
import { useNavigate } from "react-router";

import { profileApi, type ManualStatus } from "@/api/profile";
import { queryKeys } from "@/api/queryKeys";
import { Modal } from "@/components/ui";
import { activeLocale, useTranslation, type TranslationKey } from "@/i18n";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/auth";
import { Avatar } from "@/ui/avatar";
import { DropdownMenu, type MenuItem } from "@/ui/dropdown-menu";

import { statusDotClass } from "./statusDot";

const STATUSES: readonly ManualStatus[] = ["working", "break", "off"];

function useShortcutDialog(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key === "/") {
        event.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return (): void => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  return [open, setOpen];
}

export function UserMenu(): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const agencies = useAuthStore((state) => state.agencies);
  const switchAgency = useAuthStore((state) => state.switchAgency);
  const [shortcutsOpen, setShortcutsOpen] = useShortcutDialog();
  const [helpOpen, setHelpOpen] = useState(false);
  const inAgency = user?.tenant != null;

  const availability = useQuery({
    queryKey: queryKeys.profile.availability(),
    queryFn: profileApi.availability,
    enabled: inAgency,
  });
  const week = useQuery({
    queryKey: queryKeys.profile.myWeek(),
    queryFn: profileApi.myWeek,
    enabled: inAgency,
  });
  const preferences = useQuery({
    queryKey: queryKeys.profile.preferences(),
    queryFn: profileApi.preferences,
    enabled: inAgency,
  });
  const setStatus = useMutation({
    mutationFn: (status: ManualStatus) => profileApi.updateAvailability({ manual_status: status }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.profile.availability(), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.team.status() });
    },
  });

  if (user === null) return null;

  const agencyItems: readonly MenuItem[] = agencies.length > 1
    ? agencies.map((agency, index) => {
        const current = agency.id === user.tenant?.id;
        return {
          key: `agency-${agency.id}`,
          label: `${agency.name} · ${t(`role.${agency.role}` as TranslationKey)}`,
          icon: current ? <Check strokeWidth={1.5} /> : <ArrowLeftRight strokeWidth={1.5} />,
          disabled: current,
          separatorBefore: index === 0,
          onSelect: (): void => {
            switchAgency(agency.id);
          },
        };
      })
    : [];

  const manual = availability.data?.manual_status;
  const statusItems: readonly MenuItem[] = inAgency
    ? [
        ...STATUSES.map((status, index) => ({
          key: `status-${status}`,
          label: t(`status.${status}` as TranslationKey),
          icon: manual === status ? <Check strokeWidth={1.5} /> : <span className={cn("size-2 rounded-pill", statusDotClass(status))} />,
          separatorBefore: index === 0,
          onSelect: (): void => {
            setStatus.mutate(status);
          },
        })),
        { key: "status-note", kind: "text" as const, label: t("status.note") },
      ]
    : [];

  const theme = preferences.data?.theme;
  const items: readonly MenuItem[] = [
    ...statusItems,
    ...(inAgency
      ? [
          {
            key: "week",
            label: t("shell.menu.week", { count: week.data?.won ?? 0 }),
            icon: <TrendingUp strokeWidth={1.5} />,
            separatorBefore: true,
            onSelect: (): void => {
              void navigate("/hisobotlar/mening");
            },
          },
          {
            key: "profile",
            label: t("shell.menu.profileSettings"),
            icon: <UserRound strokeWidth={1.5} />,
            separatorBefore: true,
            onSelect: (): void => {
              void navigate("/profile?section=shaxsiy");
            },
          },
          {
            key: "notifications",
            label: t("shell.menu.notifications"),
            icon: <Bell strokeWidth={1.5} />,
            onSelect: (): void => {
              void navigate("/profile?section=bildirishnomalar");
            },
          },
          {
            key: "theme",
            label: t("shell.menu.theme"),
            hint: theme === undefined ? undefined : t(`theme.${theme}` as TranslationKey),
            icon: <Palette strokeWidth={1.5} />,
            onSelect: (): void => {
              void navigate("/profile?section=korinish");
            },
          },
          {
            key: "language",
            label: t("shell.menu.language"),
            hint: t(`language.${activeLocale()}` as TranslationKey),
            icon: <Languages strokeWidth={1.5} />,
            onSelect: (): void => {
              void navigate("/profile?section=korinish");
            },
          },
        ]
      : []),
    {
      key: "shortcuts",
      label: t("shell.menu.shortcuts"),
      hint: "⌘ /",
      icon: <Keyboard strokeWidth={1.5} />,
      onSelect: (): void => {
        setShortcutsOpen(true);
      },
    },
    {
      key: "help",
      label: t("shell.menu.help"),
      icon: <CircleHelp strokeWidth={1.5} />,
      onSelect: (): void => {
        setHelpOpen(true);
      },
    },
    ...(user.role === "owner"
      ? [
          {
            key: "agency-settings",
            label: t("shell.menu.agencySettings"),
            icon: <Building2 strokeWidth={1.5} />,
            separatorBefore: true,
            onSelect: (): void => {
              void navigate("/sozlamalar");
            },
          },
        ]
      : []),
    ...agencyItems,
    ...(user.is_platform_admin
      ? [
          {
            key: "platform",
            label: t("shell.menu.platform"),
            icon: <Monitor strokeWidth={1.5} />,
            onSelect: (): void => {
              void navigate("/platforma/tenantlar");
            },
          },
        ]
      : []),
    {
      key: "logout",
      label: t("auth.logout"),
      icon: <LogOut strokeWidth={1.5} />,
      separatorBefore: true,
      destructive: true,
      onSelect: (): void => {
        logout();
        void navigate("/kirish", { replace: true });
      },
    },
  ];

  const effective = availability.data?.effective_status;
  const subtitle = [
    user.role === null ? null : t(`role.${user.role}` as TranslationKey),
    user.branch?.name ?? null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  return (
    <>
      <DropdownMenu
        label={t("shell.menu.userLabel")}
        align="end"
        header={
          <div className="flex min-w-56 items-center gap-3">
            <Avatar name={user.full_name} src={user.avatar_url} size="md" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-base font-medium text-ink">{user.full_name}</span>
              {subtitle === "" ? null : <span className="truncate text-xs text-ink-muted">{subtitle}</span>}
              <span className="truncate text-xs text-ink-subtle">{user.email}</span>
            </div>
          </div>
        }
        trigger={
          <button
            type="button"
            className="relative rounded-pill focus-visible:outline-none focus-visible:shadow-halo"
          >
            <Avatar name={user.full_name} src={user.avatar_url} size="md" />
            <span
              aria-label={effective === undefined ? undefined : t(`status.${effective}` as TranslationKey)}
              role={effective === undefined ? undefined : "img"}
              className={cn(
                "absolute -bottom-0.5 -right-0.5 size-2.5 rounded-pill border-2 border-canvas",
                statusDotClass(effective),
              )}
            />
          </button>
        }
        items={items}
      />
      {shortcutsOpen ? (
        <Modal
          title={t("shell.shortcuts.title")}
          onClose={() => {
            setShortcutsOpen(false);
          }}
        >
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-base">
            <dt className="font-mono text-ink-muted">⌘ K</dt>
            <dd className="text-ink">{t("shell.shortcuts.palette")}</dd>
            <dt className="font-mono text-ink-muted">⌘ /</dt>
            <dd className="text-ink">{t("shell.shortcuts.help")}</dd>
          </dl>
        </Modal>
      ) : null}
      {helpOpen ? (
        <Modal
          title={t("shell.menu.help")}
          onClose={() => {
            setHelpOpen(false);
          }}
        >
          <p className="text-base text-ink-muted">{t("shell.help.body")}</p>
        </Modal>
      ) : null}
    </>
  );
}
